import Raven from 'raven-js';
import { useCallback, useState } from 'react';
import { debounce } from 'underscore';
import { fetchFormsFromCrmSearch } from 'ContentData/api/Forms';
import { EMBED_VERSIONS } from 'ContentUtils/constants/FormsCRM';
import { fetchFilteredForms } from 'ContentData/api/Forms';
import { HUBSPOT, TICKET_FORM } from 'ContentUtils/constants/FormTypes';
const FORM_PROPERTY_TYPE_OBJECT_ID = {
  CONTACT: '0-1',
  COMPANY: '0-2',
  TICKET: '0-5'
};
const FORMS_PER_REQUEST = 5;
const getFilterGroups = ({
  formId,
  embedVersions
}) => [...(embedVersions || []).map(v => {
  if (v === EMBED_VERSIONS.V2) {
    // v2 refers to all forms before v4. These have no value for hs_embed_version
    // There may later be a backfill of embed_version for these forms
    // Until then, we need to query for v2 as "no value for hs_embed_version"
    return {
      filters: [{
        operator: 'NOT_HAS_PROPERTY',
        property: 'hs_embed_version'
      }, {
        operator: 'EQ',
        property: 'hs_form_type',
        value: 'HUBSPOT'
      }]
    };
  }
  return {
    filters: [{
      operator: 'EQ',
      property: 'hs_embed_version',
      value: v
    }, {
      operator: 'EQ',
      property: 'hs_form_type',
      value: 'HUBSPOT'
    }]
  };
}).flat(), ...(formId ? [{
  filters: [{
    operator: 'EQ',
    property: 'hs_form_id',
    value: formId
  }]
}] : [])];
const getForms = async ({
  query = '',
  formId = '',
  offset = 0,
  embedVersions,
  requiredPropertyTypes
}) => {
  const count = offset + FORMS_PER_REQUEST;
  try {
    if (!requiredPropertyTypes) {
      const filterGroups = getFilterGroups({
        formId,
        embedVersions
      });
      const response = await fetchFormsFromCrmSearch({
        count,
        query,
        filterGroups
      });
      const {
        hasMore,
        total,
        results
      } = response;
      return {
        count,
        total,
        hasMore,
        results
      };
    }
    const formsQuery = {
      name: query,
      limit: count,
      offset: 0,
      formTypes: [HUBSPOT, TICKET_FORM],
      allowV4Forms: embedVersions === null || embedVersions === void 0 ? void 0 : embedVersions.includes(EMBED_VERSIONS.V4),
      objectTypeId: requiredPropertyTypes.map(propertyType => FORM_PROPERTY_TYPE_OBJECT_ID[propertyType])
    };
    const {
      results,
      total
    } = await fetchFilteredForms(formsQuery);
    const hasMore = results.length === count;
    const filterGroups = [{
      filters: [{
        operator: 'IN',
        property: 'hs_form_id',
        values: results.map(r => r.guid)
      }]
    }];
    const {
      results: crmResults
    } = await fetchFormsFromCrmSearch({
      count,
      filterGroups
    });
    return {
      count,
      total,
      hasMore,
      results: crmResults
    };
  } catch (error) {
    Raven.captureException(error);
    return {
      count,
      total: count,
      hasMore: true,
      results: []
    };
  }
};
const useForms = () => {
  const [results, setResults] = useState([]);
  const getAll = useCallback(options => getForms(options).then(res => {
    setResults(res.results);
    return res;
  }).catch(e => {
    setResults([]);
    return e;
  }), []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getAllDebounced = useCallback(debounce((options, cb) => {
    getAll(options).then(res => {
      cb(res);
    }).catch(() => cb({
      count: 0,
      total: 0,
      hasMore: true,
      results: []
    }));
  }, 300), [getAll]);
  const getByFormId = useCallback(formId => {
    const fromResults = results.find(r => r.properties.hs_form_id.value === formId);
    if (fromResults) {
      return Promise.resolve(fromResults);
    }
    return getForms({
      formId
    }).then(({
      results: _results
    }) => _results[0]);
  }, [results]);
  return {
    getAll,
    getAllDebounced,
    getByFormId,
    results
  };
};
export default useForms;